import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { eq, useLiveQuery } from "@tanstack/react-db";
import { createContext, memo, useMemo } from "react";
import type { DraftCollectionItem } from "../db/collections";
import { useGlobalSupportTicketCollections } from "./global-support-ticket-collections-context";

export interface SupportTicketDraftsContextValue {
  drafts: DraftCollectionItem[];
  isLoading: boolean;
}

export const SupportTicketDraftsContext = createContext<
  SupportTicketDraftsContextValue | undefined
>(undefined);
SupportTicketDraftsContext.displayName = "SupportTicketDraftsContext";

export function useSupportTicketDrafts(): SupportTicketDraftsContextValue {
  return useRequiredContext(SupportTicketDraftsContext);
}

interface SupportTicketDraftsProviderProps {
  supportTicketId: SupportTicketId;
  children: React.ReactNode;
}

/**
 * Provider that fetches drafts once at the support ticket level.
 * The list index draft count can lag behind CRDB after local draft writes and
 * asynchronous send failures, so the detail view always queries the canonical
 * draft source.
 */
export const SupportTicketDraftsProvider = memo(
  function SupportTicketDraftsProvider({
    supportTicketId,
    children,
  }: SupportTicketDraftsProviderProps) {
    const collections = useGlobalSupportTicketCollections();

    const result = useLiveQuery(
      (q) =>
        q
          .from({ draft: collections.drafts })
          .where(({ draft }) =>
            eq(draft.supportTicketId, String(supportTicketId)),
          ),
      [supportTicketId],
    );

    const value = useMemo(
      (): SupportTicketDraftsContextValue => ({
        drafts: result.data ?? [],
        isLoading: result.isLoading,
      }),
      [result.data, result.isLoading],
    );

    return (
      <SupportTicketDraftsContext.Provider value={value}>
        {children}
      </SupportTicketDraftsContext.Provider>
    );
  },
);
